import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { trackAssessmentComplete, trackAssessmentStart, trackAssessmentViewed, trackAssessmentStep, trackAssessmentAbandoned, trackLeadSubmitted } from "../lib/analytics";
import { useAuth } from "../context/AuthContext";
import { ChevronRight, ChevronLeft, Loader2, User, Calendar, AlertCircle, Pill, Baby, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const hexToHsl = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return null;
  const [r, g, b] = [m[1], m[2], m[3]].map((v) => parseInt(v, 16) / 255);
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

const WELLNESS_GOALS = [
  "High Blood Pressure",
  "Parasite Cleanse",
  "Digestive Issues",
  "Sleep Problems",
  "Stress & Anxiety",
  "Immune Support",
  "Joint Pain",
  "Skin Health",
  "Energy & Vitality",
  "Heart Health",
  "Brain Function",
  "Weight Management",
  "Hormonal Balance",
  "Respiratory Health",
  "Liver Support",
  "Kidney Health"
];

const Questionnaire = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasSignedAgreement, loading: authLoading } = useAuth();
  const [step, setStep] = useState(1);
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [customAilment, setCustomAilment] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [weight, setWeight] = useState("");
  const [weightUnit, setWeightUnit] = useState("lbs");
  const [bodyType, setBodyType] = useState("");
  const [bloodType, setBloodType] = useState("");
  const [preparationMethod, setPreparationMethod] = useState("");
  const [currentAilments, setCurrentAilments] = useState("");
  const [currentMedications, setCurrentMedications] = useState("");
  const [isPregnant, setIsPregnant] = useState("");
  const [isTryingToConceive, setIsTryingToConceive] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const [leadName, setLeadName] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [leadPhone, setLeadPhone] = useState("");
  const [leadConsent, setLeadConsent] = useState(false);
  const [wlBrand, setWlBrand] = useState(null);

  useEffect(() => {
    const key = sessionStorage.getItem("wl_embed_key");
    if (!key) return;
    axios.get(`${API}/wl/embed/${key}/config`)
      .then(({ data }) => setWlBrand({ ...data, key }))
      .catch(() => sessionStorage.removeItem("wl_embed_key"));
  }, []);

  const wlHsl = wlBrand ? hexToHsl(wlBrand.button_colour || wlBrand.primary_colour) : null;
  const wlStyle = wlHsl ? { "--primary": wlHsl } : undefined;

  const wlBrandBar = wlBrand && (
    <div className="flex items-center gap-3 bg-white border border-gray-100 rounded-2xl px-4 py-3 shadow-sm mb-4"
         style={{ borderLeft: `5px solid ${wlBrand.primary_colour || "#047857"}` }} data-testid="wl-brand-bar">
      {wlBrand.has_logo && (
        <img src={`${API}/wl/embed/${wlBrand.key}/logo`} alt={`${wlBrand.store_name} logo`}
             className="h-9 w-9 rounded-lg object-contain bg-gray-50 p-0.5"
             onError={(e) => { e.target.style.display = "none"; }} />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-gray-900 truncate" data-testid="wl-brand-store-name">{wlBrand.store_name}</p>
        <p className="text-[10px] text-gray-400">{wlBrand.assessment_name} · Powered by Elite Herbalist</p>
      </div>
      {wlBrand.sandbox && <span className="text-[9px] font-bold bg-amber-100 text-amber-800 rounded-full px-2 py-0.5 shrink-0">SANDBOX</span>}
    </div>
  );

  useEffect(() => {
    if (user) {
      setLeadName((n) => n || user.full_name || "");
      setLeadEmail((e) => e || user.email || "");
    }
  }, [user]);

  const needsLead = !authLoading && !(user && hasSignedAgreement) && !leadDone;

  const handleLeadContinue = () => {
    if (!leadName.trim()) { toast.error("Please enter your full name"); return; }
    if (!/^\S+@\S+\.\S+$/.test(leadEmail.trim())) { toast.error("Please enter a valid email address"); return; }
    if (!leadConsent) { toast.error("Please tick the consent box to continue"); return; }
    trackLeadSubmitted();
    axios.post(`${API}/leads/start`, {
      full_name: leadName.trim(), email: leadEmail.trim(),
      phone: leadPhone.trim() || null, consent: true,
    }).catch(() => {});
    setLeadDone(true);
  };

  useEffect(() => { trackAssessmentStart(); }, []);

  const stepRef = useRef(1);
  useEffect(() => {
    const prev = stepRef.current;
    if (step > prev) trackAssessmentStep(prev, "completed");
    if (step !== prev) trackAssessmentStep(step, "viewed");
    stepRef.current = step;
  }, [step]);

  useEffect(() => {
    trackAssessmentViewed();
    trackAssessmentStep(1, "viewed");
    const armedAt = Date.now();
    const onLeave = () => trackAssessmentAbandoned(stepRef.current);
    window.addEventListener("pagehide", onLeave);
    return () => {
      window.removeEventListener("pagehide", onLeave);
      if (Date.now() - armedAt > 1500) trackAssessmentAbandoned(stepRef.current);
    };
  }, []);

  // Check if carbonated mode is requested from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('mode') === 'carbonated') {
      setPreparationMethod('Carbonated');
    }
  }, [location]);

  const totalSteps = gender === "Female" ? 7 : 6;
  const progress = ((step - 0.5) / totalSteps) * 100;
  const stepLabels = gender === "Female"
    ? ["Your goals", "Your age", "About you", "Body profile", "How you'll take herbs", "Health & safety", "Final check"]
    : ["Your goals", "Your age", "About you", "Body profile", "How you'll take herbs", "Health & safety"];

  const handleGoalToggle = (goal) => {
    if (selectedGoals.includes(goal)) {
      setSelectedGoals(selectedGoals.filter(g => g !== goal));
    } else {
      setSelectedGoals([...selectedGoals, goal]);
    }
  };

  const handleNext = () => {
    if (step === 1 && selectedGoals.length === 0 && !customAilment.trim()) {
      toast.error("Please select at least one wellness goal or enter a custom ailment");
      return;
    }
    if (step === 2 && (!age || parseInt(age) < 18 || parseInt(age) > 120)) {
      toast.error("You must be 18 or older to take the assessment (18-120)");
      return;
    }
    if (step === 3 && !gender) {
      toast.error("Please select your gender");
      return;
    }
    if (step === 4 && weight && (parseFloat(weight) < 20 || parseFloat(weight) > 400)) {
      toast.error("Please enter a realistic weight, or leave it blank");
      return;
    }
    if (step === 5 && !preparationMethod) {
      toast.error("Please select a preparation method");
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/questionnaire/submit`, {
        wellness_goals: selectedGoals,
        custom_ailment: customAilment.trim() || null,
        age: parseInt(age),
        gender: gender,
        weight: weight ? parseFloat(weight) : null,
        body_type: bodyType,
        blood_type: bloodType,
        preparation_method: preparationMethod,
        current_ailments: currentAilments.trim() || null,
        current_medications: currentMedications.trim() || null,
        is_pregnant: gender === "Female" ? isPregnant === "yes" : false,
        is_trying_to_conceive: gender === "Female" ? isTryingToConceive === "yes" : false,
        lead_name: leadName.trim() || null,
        lead_email: leadEmail.trim() || null,
        lead_phone: leadPhone.trim() || null,
        lead_consent: leadConsent,
        wl_key: sessionStorage.getItem('wl_embed_key') || null
      });

      sessionStorage.setItem('recommendations', JSON.stringify(response.data));
      if (wlBrand) {
        sessionStorage.setItem('wl_brand_banner', JSON.stringify({ store_name: wlBrand.store_name, primary_colour: wlBrand.primary_colour, key: wlBrand.key, has_logo: wlBrand.has_logo }));
        sessionStorage.removeItem('wl_embed_key');
      }
      trackAssessmentStep(stepRef.current, "completed");
      trackAssessmentComplete();
      const emailTo = leadEmail.trim();
      toast.success(emailTo
        ? `Recommendations ready! A copy has been emailed to ${emailTo}.`
        : "Recommendations generated successfully!");
      navigate('/recommendations');
    } catch (error) {
      console.error('Error submitting questionnaire:', error);
      toast.error("Failed to generate recommendations. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (needsLead) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center py-12 px-4" style={wlStyle}>
        <div className="w-full max-w-xl">
          {wlBrandBar}
          <div className="flex justify-end mb-3">
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-1.5 text-gray-400 hover:text-gray-700 text-sm font-medium transition-colors bg-white border border-gray-200 rounded-full px-3 py-1.5 shadow-sm"
              data-testid="lead-exit-btn" aria-label="Exit assessment"
            >
              <X className="w-4 h-4" /> Exit
            </button>
          </div>
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8" data-testid="lead-capture-card">
            <div className="text-center mb-6">
              <h1 className="font-playfair text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Before we begin</h1>
              <p className="text-sm text-gray-500">
                Tell us where to send your personalised recommendations. No account or password needed.
              </p>
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="lead-name" className="text-sm font-semibold text-gray-700">Full name *</Label>
                <Input id="lead-name" value={leadName} onChange={(e) => setLeadName(e.target.value)}
                  placeholder="Jane Smith" className="mt-1.5 rounded-xl" data-testid="lead-name-input" />
              </div>
              <div>
                <Label htmlFor="lead-email" className="text-sm font-semibold text-gray-700">Email address *</Label>
                <Input id="lead-email" type="email" value={leadEmail} onChange={(e) => setLeadEmail(e.target.value)}
                  placeholder="jane@example.com" className="mt-1.5 rounded-xl" data-testid="lead-email-input" />
              </div>
              <div>
                <Label htmlFor="lead-phone" className="text-sm font-semibold text-gray-700">Phone number <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Input id="lead-phone" type="tel" value={leadPhone} onChange={(e) => setLeadPhone(e.target.value)}
                  placeholder="+44 7700 900000" className="mt-1.5 rounded-xl" data-testid="lead-phone-input" />
              </div>
              <label className="flex items-start gap-3 cursor-pointer bg-emerald-50/60 border border-emerald-100 rounded-xl p-3.5">
                <input type="checkbox" checked={leadConsent} onChange={(e) => setLeadConsent(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-emerald-700 shrink-0" data-testid="lead-consent-checkbox" />
                <span className="text-xs text-gray-600 leading-relaxed">
                  I understand this is educational wellness guidance, not medical advice, I consent to Elite Herbalist processing the health-related answers I provide to generate my educational recommendations, I confirm I am 18 or over, and I accept the terms. *
                </span>
              </label>
              <Button onClick={handleLeadContinue} data-testid="lead-continue-btn"
                className="w-full rounded-full bg-gradient-to-r from-primary to-emerald-600 hover:from-emerald-700 hover:to-emerald-800 text-white font-bold py-6 text-base">
                Start My Free Assessment <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
              <p className="text-[11px] text-gray-400 text-center leading-relaxed">
                Your details are stored securely, used only for your recommendations and wellness emails, and never sold.
                Recommendations are generated by an automated AI system — educational guidance, not a human herbalist consultation.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center py-12 px-4" style={wlStyle}>
      <div className="w-full max-w-2xl">
        {wlBrandBar}
        {/* Exit button */}
        <div className="flex justify-end mb-3">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-1.5 text-gray-400 hover:text-gray-700 text-sm font-medium transition-colors bg-white border border-gray-200 rounded-full px-3 py-1.5 shadow-sm"
            data-testid="questionnaire-exit-btn"
            aria-label="Exit assessment"
          >
            <X className="w-4 h-4" /> Exit
          </button>
        </div>
        {/* Progress Bar */}
        <div className="mb-8" data-testid="progress-bar">
          <div className="flex items-end justify-between mb-2">
            <p className="text-xs font-bold text-primary uppercase tracking-wide">
              Step {step} of {totalSteps} · <span className="text-gray-700 normal-case tracking-normal font-semibold">{stepLabels[step - 1]}</span>
            </p>
            <p className="text-xs font-semibold text-gray-400" data-testid="progress-percent">{Math.round(progress)}%</p>
          </div>
          <Progress value={progress} className="h-2" />
          <div className="flex justify-between mt-2" data-testid="progress-dots">
            {stepLabels.map((label, i) => (
              <span key={label} title={label}
                    className={`w-2 h-2 rounded-full transition-colors ${i + 1 < step ? "bg-primary" : i + 1 === step ? "bg-primary ring-4 ring-primary/20" : "bg-gray-200"}`} />
            ))}
          </div>
          <p className="text-[11px] text-gray-400 mt-2.5 text-center">
            Your answers stay private · Use Back to change anything · Takes about 2 minutes
          </p>
        </div>

        {/* Card Container */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 md:p-12">
          {step === 1 && (
            <div className="space-y-6" data-testid="step-wellness-goals">
              <div>
                <h2 className="font-playfair text-3xl font-bold text-gray-900 mb-2">
                  What are your wellness goals?
                </h2>
                <p className="text-gray-600">
                  Select all that apply to receive personalized recommendations.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {WELLNESS_GOALS.map((goal) => (
                  <button
                    key={goal}
                    onClick={() => handleGoalToggle(goal)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      selectedGoals.includes(goal)
                        ? "border-primary bg-secondary text-primary"
                        : "border-gray-200 hover:border-primary/30 text-gray-700"
                    }`}
                    data-testid={`goal-option-${goal.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {goal}
                  </button>
                ))}
              </div>

              <div className="space-y-2">
                <Label htmlFor="custom-ailment" className="text-gray-700">
                  Or enter a custom wellness goal:
                </Label>
                <Input
                  id="custom-ailment"
                  placeholder="E.g., Chronic fatigue, allergies..."
                  value={customAilment}
                  onChange={(e) => setCustomAilment(e.target.value)}
                  className="h-12"
                  data-testid="custom-ailment-input"
                />
              </div>

              <Button
                onClick={handleNext}
                className="w-full h-12 rounded-full"
                size="lg"
                data-testid="next-btn-step1"
              >
                Continue
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6" data-testid="step-age">
              <div>
                <h2 className="font-playfair text-3xl font-bold text-gray-900 mb-2">
                  Tell us about yourself
                </h2>
                <p className="text-gray-600">
                  This helps us provide age-appropriate recommendations.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="age" className="text-gray-700 flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-primary" />
                  What is your age?
                </Label>
                <Input
                  id="age"
                  type="number"
                  min="15"
                  max="120"
                  placeholder="Enter your age"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  className="h-12 text-lg"
                  data-testid="age-input"
                />
              </div>

              <div className="flex gap-4">
                <Button
                  onClick={handleBack}
                  variant="outline"
                  className="flex-1 h-12 rounded-full"
                  data-testid="back-btn-step2"
                >
                  <ChevronLeft className="mr-2 w-5 h-5" />
                  Back
                </Button>
                <Button
                  onClick={handleNext}
                  className="flex-1 h-12 rounded-full"
                  data-testid="next-btn-step2"
                >
                  Continue
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6" data-testid="step-gender">
              <div>
                <h2 className="font-playfair text-3xl font-bold text-gray-900 mb-2">
                  What is your gender?
                </h2>
                <p className="text-gray-600">
                  This helps us provide more accurate recommendations.
                </p>
              </div>

              <RadioGroup value={gender} onValueChange={setGender}>
                <div className="space-y-3">
                  <label
                    className={`flex items-center p-6 rounded-lg border-2 cursor-pointer transition-all ${
                      gender === "Male"
                        ? "border-primary bg-secondary"
                        : "border-gray-200 hover:border-primary/30"
                    }`}
                    data-testid="gender-male"
                  >
                    <RadioGroupItem value="Male" id="male" />
                    <Label htmlFor="male" className="ml-3 cursor-pointer text-lg">
                      Male
                    </Label>
                  </label>

                  <label
                    className={`flex items-center p-6 rounded-lg border-2 cursor-pointer transition-all ${
                      gender === "Female"
                        ? "border-primary bg-secondary"
                        : "border-gray-200 hover:border-primary/30"
                    }`}
                    data-testid="gender-female"
                  >
                    <RadioGroupItem value="Female" id="female" />
                    <Label htmlFor="female" className="ml-3 cursor-pointer text-lg">
                      Female
                    </Label>
                  </label>
                </div>
              </RadioGroup>

              <div className="flex gap-4">
                <Button
                  onClick={handleBack}
                  variant="outline"
                  className="flex-1 h-12 rounded-full"
                  data-testid="back-btn-step3"
                >
                  <ChevronLeft className="mr-2 w-5 h-5" />
                  Back
                </Button>
                <Button
                  onClick={handleNext}
                  className="flex-1 h-12 rounded-full"
                  data-testid="next-btn-step3"
                >
                  Continue
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6" data-testid="step-body-info">
              <div>
                <h2 className="font-playfair text-3xl font-bold text-gray-900 mb-2">
                  Body Information
                </h2>
                <p className="text-gray-600">
                  Help us personalize recommendations based on your body type and constitution.
                </p>
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="weight" className="text-gray-700 flex items-center">
                    <User className="w-4 h-4 mr-2 text-primary" />
                    Weight
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="weight"
                      type="number"
                      min={weightUnit === "lbs" ? "50" : "20"}
                      max={weightUnit === "lbs" ? "500" : "230"}
                      placeholder={`Enter weight in ${weightUnit}`}
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      className="h-12 text-lg flex-1"
                      data-testid="weight-input"
                    />
                    <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setWeightUnit("lbs")}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${
                          weightUnit === "lbs"
                            ? "bg-primary text-white"
                            : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        lbs
                      </button>
                      <button
                        type="button"
                        onClick={() => setWeightUnit("kg")}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${
                          weightUnit === "kg"
                            ? "bg-primary text-white"
                            : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        kg
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700">Body Type</Label>
                  <RadioGroup value={bodyType} onValueChange={setBodyType}>
                    <div className="space-y-3">
                      <label
                        className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          bodyType === "Ectomorph"
                            ? "border-primary bg-secondary"
                            : "border-gray-200 hover:border-primary/30"
                        }`}
                        data-testid="body-type-ectomorph"
                      >
                        <RadioGroupItem value="Ectomorph" id="ectomorph" />
                        <div className="ml-3">
                          <Label htmlFor="ectomorph" className="cursor-pointer font-semibold">
                            Ectomorph
                          </Label>
                          <p className="text-sm text-gray-600">Lean, fast metabolism, difficulty gaining weight</p>
                        </div>
                      </label>

                      <label
                        className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          bodyType === "Mesomorph"
                            ? "border-primary bg-secondary"
                            : "border-gray-200 hover:border-primary/30"
                        }`}
                        data-testid="body-type-mesomorph"
                      >
                        <RadioGroupItem value="Mesomorph" id="mesomorph" />
                        <div className="ml-3">
                          <Label htmlFor="mesomorph" className="cursor-pointer font-semibold">
                            Mesomorph
                          </Label>
                          <p className="text-sm text-gray-600">Athletic build, gains muscle easily, balanced metabolism</p>
                        </div>
                      </label>

                      <label
                        className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          bodyType === "Endomorph"
                            ? "border-primary bg-secondary"
                            : "border-gray-200 hover:border-primary/30"
                        }`}
                        data-testid="body-type-endomorph"
                      >
                        <RadioGroupItem value="Endomorph" id="endomorph" />
                        <div className="ml-3">
                          <Label htmlFor="endomorph" className="cursor-pointer font-semibold">
                            Endomorph
                          </Label>
                          <p className="text-sm text-gray-600">Larger build, stores fat easily, slower metabolism</p>
                        </div>
                      </label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700">Blood Type</Label>
                  <RadioGroup value={bloodType} onValueChange={setBloodType}>
                    <div className="grid grid-cols-3 gap-3">
                      {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "Unknown"].map((type) => (
                        <label
                          key={type}
                          className={`flex items-center justify-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            bloodType === type
                              ? "border-primary bg-secondary"
                              : "border-gray-200 hover:border-primary/30"
                          } ${type === "Unknown" ? "col-span-3 bg-gray-50" : ""}`}
                          data-testid={`blood-type-${type}`}
                        >
                          <RadioGroupItem value={type} id={type} className="sr-only" />
                          <Label htmlFor={type} className={`cursor-pointer ${type === "Unknown" ? "text-gray-500" : "font-semibold"}`}>
                            {type === "Unknown" ? "I don't know my blood type" : type}
                          </Label>
                        </label>
                      ))}
                    </div>
                  </RadioGroup>
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  onClick={handleBack}
                  variant="outline"
                  className="flex-1 h-12 rounded-full"
                  data-testid="back-btn-step4"
                >
                  <ChevronLeft className="mr-2 w-5 h-5" />
                  Back
                </Button>
                <Button
                  onClick={handleNext}
                  className="flex-1 h-12 rounded-full"
                  data-testid="next-btn-step4"
                >
                  Continue
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-6" data-testid="step-preparation-method">
              <div>
                <h2 className="font-playfair text-3xl font-bold text-gray-900 mb-2">
                  Preferred Preparation Method
                </h2>
                <p className="text-gray-600">
                  How would you like to enjoy your herbal recommendations?
                </p>
              </div>

              <RadioGroup value={preparationMethod} onValueChange={setPreparationMethod}>
                <div className="space-y-3">
                  <label
                    className={`flex items-start p-5 rounded-lg border-2 cursor-pointer transition-all ${
                      preparationMethod === "Traditional"
                        ? "border-primary bg-secondary"
                        : "border-gray-200 hover:border-primary/30"
                    }`}
                    data-testid="prep-traditional"
                  >
                    <RadioGroupItem value="Traditional" id="traditional" className="mt-1" />
                    <div className="ml-3">
                      <Label htmlFor="traditional" className="cursor-pointer font-semibold text-base">
                        Traditional (Tea, Tincture, Capsule)
                      </Label>
                      <p className="text-sm text-gray-600 mt-1">Classic herbal preparations - teas, tinctures, decoctions, and capsules</p>
                    </div>
                  </label>

                  <label
                    className={`flex items-start p-5 rounded-lg border-2 cursor-pointer transition-all ${
                      preparationMethod === "Carbonated"
                        ? "border-primary bg-secondary"
                        : "border-gray-200 hover:border-primary/30"
                    }`}
                    data-testid="prep-carbonated"
                  >
                    <RadioGroupItem value="Carbonated" id="carbonated" className="mt-1" />
                    <div className="ml-3">
                      <Label htmlFor="carbonated" className="cursor-pointer font-semibold text-base">
                        Carbonated / Sparkling
                      </Label>
                      <p className="text-sm text-gray-600 mt-1">Sparkling herbal beverages using CO₂ infusion systems (e.g., Infuso Soda Machine)</p>
                    </div>
                  </label>
                </div>
              </RadioGroup>

              <div className="flex gap-4">
                <Button
                  onClick={handleBack}
                  variant="outline"
                  className="flex-1 h-12 rounded-full"
                  data-testid="back-btn-step5"
                >
                  <ChevronLeft className="mr-2 w-5 h-5" />
                  Back
                </Button>
                <Button
                  onClick={handleNext}
                  className="flex-1 h-12 rounded-full"
                  data-testid="next-btn-step5"
                >
                  Continue
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-6" data-testid="step-health-info">
              <div>
                <h2 className="font-playfair text-3xl font-bold text-gray-900 mb-2">
                  Health Information
                </h2>
                <p className="text-gray-600">
                  This helps us ensure safe recommendations.
                </p>
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="current-ailments" className="text-gray-700 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-2 text-primary" />
                    Do you have any current medical conditions?
                  </Label>
                  <Textarea
                    id="current-ailments"
                    placeholder="E.g., Diabetes, high blood pressure, kidney disease, allergies... (Optional but strongly recommended)"
                    value={currentAilments}
                    onChange={(e) => setCurrentAilments(e.target.value)}
                    className="min-h-[100px] text-base"
                    data-testid="current-ailments-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="current-medications" className="text-gray-700 flex items-center">
                    <Pill className="w-4 h-4 mr-2 text-primary" />
                    Are you currently taking any medications or supplements?
                  </Label>
                  <Textarea
                    id="current-medications"
                    placeholder="E.g., Blood thinners (Warfarin), blood pressure meds (Lisinopril), vitamins... (Optional but strongly recommended for safety)"
                    value={currentMedications}
                    onChange={(e) => setCurrentMedications(e.target.value)}
                    className="min-h-[100px] text-base"
                    data-testid="current-medications-input"
                  />
                  <p className="text-xs text-gray-500">
                    This information helps us identify potential herb-drug interactions.
                  </p>
                </div>

                {gender === "Female" && (
                  <div className="space-y-4 bg-pink-50 border border-pink-200 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <Baby className="w-5 h-5 text-pink-600 mr-2" />
                      <h3 className="font-semibold text-gray-900">Women's Health</h3>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-gray-700">Are you currently pregnant?</Label>
                      <RadioGroup value={isPregnant} onValueChange={setIsPregnant}>
                        <div className="flex gap-4">
                          <label className="flex items-center cursor-pointer">
                            <RadioGroupItem value="yes" id="pregnant-yes" />
                            <Label htmlFor="pregnant-yes" className="ml-2 cursor-pointer">Yes</Label>
                          </label>
                          <label className="flex items-center cursor-pointer">
                            <RadioGroupItem value="no" id="pregnant-no" />
                            <Label htmlFor="pregnant-no" className="ml-2 cursor-pointer">No</Label>
                          </label>
                        </div>
                      </RadioGroup>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-gray-700">Are you trying to conceive?</Label>
                      <RadioGroup value={isTryingToConceive} onValueChange={setIsTryingToConceive}>
                        <div className="flex gap-4">
                          <label className="flex items-center cursor-pointer">
                            <RadioGroupItem value="yes" id="conceive-yes" />
                            <Label htmlFor="conceive-yes" className="ml-2 cursor-pointer">Yes</Label>
                          </label>
                          <label className="flex items-center cursor-pointer">
                            <RadioGroupItem value="no" id="conceive-no" />
                            <Label htmlFor="conceive-no" className="ml-2 cursor-pointer">No</Label>
                          </label>
                        </div>
                      </RadioGroup>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-4">
                <Button
                  onClick={handleBack}
                  variant="outline"
                  className="flex-1 h-12 rounded-full"
                  data-testid="back-btn-step6"
                >
                  <ChevronLeft className="mr-2 w-5 h-5" />
                  Back
                </Button>
                <Button
                  onClick={gender === "Female" ? handleNext : handleSubmit}
                  className="flex-1 h-12 rounded-full"
                  disabled={isSubmitting && gender === "Male"}
                  data-testid="next-btn-step6"
                >
                  {gender === "Male" && isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                      Generating...
                    </>
                  ) : gender === "Female" ? (
                    <>
                      Continue
                      <ChevronRight className="ml-2 w-5 h-5" />
                    </>
                  ) : (
                    "Get My Recommendations"
                  )}
                </Button>
              </div>
            </div>
          )}

          {step === 7 && gender === "Female" && (
            <div className="space-y-6" data-testid="step-disclaimer">
              <div>
                <h2 className="font-playfair text-3xl font-bold text-gray-900 mb-4">
                  Important Disclaimer
                </h2>
                <div className="bg-secondary/50 border border-primary/20 rounded-lg p-6 space-y-4 text-gray-700">
                  <p className="leading-relaxed">
                    <strong className="text-primary">Educational Purposes Only:</strong> The information provided by Elite Herbalist is for educational purposes only and is not intended as medical advice, diagnosis, or treatment.
                  </p>
                  <p className="leading-relaxed">
                    <strong className="text-primary">Consult Healthcare Professionals:</strong> Always seek the advice of your physician or other qualified health provider with any questions you may have regarding a medical condition or before starting any new herbal regimen.
                  </p>
                  <p className="leading-relaxed">
                    <strong className="text-primary">Pregnancy & Fertility:</strong> Many herbs can affect pregnancy and fertility. If you are pregnant or trying to conceive, consult your healthcare provider before using any herbal products.
                  </p>
                  <p className="leading-relaxed">
                    <strong className="text-primary">Potential Interactions:</strong> Herbs can interact with medications and other supplements. Inform your healthcare provider about all herbs and supplements you are taking.
                  </p>
                  <p className="leading-relaxed text-sm mt-4">
                    By continuing, you acknowledge that you have read and understood this disclaimer and agree to use the information provided responsibly.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  onClick={handleBack}
                  variant="outline"
                  className="flex-1 h-12 rounded-full"
                  disabled={isSubmitting}
                  data-testid="back-btn-step7"
                >
                  <ChevronLeft className="mr-2 w-5 h-5" />
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  className="flex-1 h-12 rounded-full"
                  disabled={isSubmitting}
                  data-testid="submit-questionnaire-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    "Get My Recommendations"
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Questionnaire;